/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { socialApi } from '../../services/socialApi';
import type { Post, Comment, UserPublicProfile } from '../../types';

interface SocialState {
  posts: Post[];
  currentUserProfile: UserPublicProfile | null;
  userPosts: Post[];
  userPostsTotal: number;
  comments: Record<string, Comment[]>;
  feedPage: number;
  feedTotal: number;
  isLoading: boolean;
  isLoadingMore: boolean;
  isCommentsLoading: boolean;
  error: string | null;
}

const initialState: SocialState = {
  posts: [],
  currentUserProfile: null,
  userPosts: [],
  userPostsTotal: 0,
  comments: {},
  feedPage: 1,
  feedTotal: 0,
  isLoading: false,
  isLoadingMore: false,
  isCommentsLoading: false,
  error: null,
};

export const fetchFeed = createAsyncThunk(
  'social/fetchFeed',
  async ({ page, pageSize }: { page?: number; pageSize?: number } = {}, { rejectWithValue }) => {
    try {
      return await socialApi.getFeed(page, pageSize);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load feed');
    }
  }
);

export const createPost = createAsyncThunk(
  'social/createPost',
  async (data: { content: string; imageUrl?: string }, { rejectWithValue }) => {
    try {
      return await socialApi.createPost(data);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to create post');
    }
  }
);

export const toggleLike = createAsyncThunk(
  'social/toggleLike',
  async ({ postId, isLiked }: { postId: string; isLiked: boolean }, { rejectWithValue }) => {
    try {
      if (isLiked) {
        await socialApi.unlikePost(postId);
      } else {
        await socialApi.likePost(postId);
      }
      return { postId, isLiked: !isLiked };
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to toggle like');
    }
  }
);

export const fetchComments = createAsyncThunk(
  'social/fetchComments',
  async (postId: string, { rejectWithValue }) => {
    try {
      const comments = await socialApi.getComments(postId);
      return { postId, comments };
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load comments');
    }
  }
);

export const addComment = createAsyncThunk(
  'social/addComment',
  async ({ postId, content }: { postId: string; content: string }, { rejectWithValue }) => {
    try {
      const comment = await socialApi.addComment(postId, content);
      return { postId, comment };
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to add comment');
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  'social/fetchUserProfile',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await socialApi.getUserProfile(userId);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load profile');
    }
  }
);

export const fetchUserPosts = createAsyncThunk(
  'social/fetchUserPosts',
  async (userId: string, { rejectWithValue }) => {
    try {
      return await socialApi.getUserPosts(userId);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load user posts');
    }
  }
);

export const toggleFollow = createAsyncThunk(
  'social/toggleFollow',
  async ({ userId, isFollowing }: { userId: string; isFollowing: boolean }, { rejectWithValue }) => {
    try {
      if (isFollowing) {
        await socialApi.unfollowUser(userId);
      } else {
        await socialApi.followUser(userId);
      }
      return { userId, isFollowing: !isFollowing };
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to toggle follow');
    }
  }
);

const socialSlice = createSlice({
  name: 'social',
  initialState,
  reducers: {
    clearSocialError: (state) => {
      state.error = null;
    },
    resetFeed: (state) => {
      state.posts = [];
      state.feedPage = 1;
      state.feedTotal = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch Feed
    builder.addCase(fetchFeed.pending, (state, action) => {
      if (action.meta.arg?.page && action.meta.arg.page > 1) {
        state.isLoadingMore = true;
      } else {
        state.isLoading = true;
      }
      state.error = null;
    });
    builder.addCase(fetchFeed.fulfilled, (state, action) => {
      const page = action.meta.arg?.page || 1;
      if (page > 1) {
        state.posts = [...state.posts, ...action.payload.posts];
        state.isLoadingMore = false;
      } else {
        state.posts = action.payload.posts;
        state.isLoading = false;
      }
      state.feedTotal = action.payload.total;
      state.feedPage = page;
    });
    builder.addCase(fetchFeed.rejected, (state, action) => {
      state.isLoading = false;
      state.isLoadingMore = false;
      state.error = action.payload as string;
    });

    // Create Post
    builder.addCase(createPost.fulfilled, (state, action) => {
      state.posts = [action.payload, ...state.posts];
    });

    // Toggle Like
    builder.addCase(toggleLike.fulfilled, (state, action) => {
      const { postId, isLiked } = action.payload;
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.isLiked = isLiked;
        post.likesCount += isLiked ? 1 : -1;
      }
    });

    // Fetch Comments
    builder.addCase(fetchComments.pending, (state) => {
      state.isCommentsLoading = true;
    });
    builder.addCase(fetchComments.fulfilled, (state, action) => {
      state.comments[action.payload.postId] = action.payload.comments;
      state.isCommentsLoading = false;
    });
    builder.addCase(fetchComments.rejected, (state) => {
      state.isCommentsLoading = false;
    });

    // Add Comment
    builder.addCase(addComment.fulfilled, (state, action) => {
      const { postId, comment } = action.payload;
      if (!state.comments[postId]) {
        state.comments[postId] = [];
      }
      state.comments[postId].push(comment);
      const post = state.posts.find((p) => p.id === postId);
      if (post) {
        post.commentsCount += 1;
      }
    });

    // Fetch User Profile
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchUserProfile.fulfilled, (state, action) => {
      state.currentUserProfile = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchUserProfile.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Fetch User Posts
    builder.addCase(fetchUserPosts.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchUserPosts.fulfilled, (state, action) => {
      state.userPosts = action.payload.posts;
      state.userPostsTotal = action.payload.total;
      state.isLoading = false;
    });
    builder.addCase(fetchUserPosts.rejected, (state) => {
      state.isLoading = false;
    });

    // Toggle Follow
    builder.addCase(toggleFollow.fulfilled, (state, action) => {
      if (state.currentUserProfile && state.currentUserProfile.id === action.payload.userId) {
        state.currentUserProfile.isFollowing = action.payload.isFollowing;
        state.currentUserProfile.followersCount += action.payload.isFollowing ? 1 : -1;
      }
    });
  },
});

export const { clearSocialError, resetFeed } = socialSlice.actions;
export default socialSlice.reducer;
